/**
 * The backend's refusal of a checkout by someone who already has a
 * HeapsterLive subscription (#2218). It answers `409` with the body's `error`
 * set to `CONFLICT` and `context.code` set to
 * `HEAPSTERLIVE_SUBSCRIPTION_EXISTS`. `context.reason` says which case it is,
 * and both are resolved in the billing portal (`POST /v1/billing/portal`).
 *
 * Matched on `context.code`, never on the status alone (other 409s exist) and
 * never on the message (prose is not a contract). Duck-typed rather than
 * `instanceof ApiHttpError`, so it holds wherever the error was constructed.
 */

export const HEAPSTERLIVE_SUBSCRIPTION_EXISTS =
  'HEAPSTERLIVE_SUBSCRIPTION_EXISTS';

export type CheckoutConflictReason =
  | 'existing_subscription'
  | 'payment_required';

export const CHECKOUT_CONFLICT_MESSAGES: Record<
  CheckoutConflictReason,
  string
> = {
  existing_subscription: 'You already have a subscription.',
  payment_required: 'Your payment method needs updating.',
};

/** The button that opens the billing portal, wherever it is offered. */
export const MANAGE_SUBSCRIPTION_LABEL = 'Manage subscription';

/** Shown where Subscribe stays disabled because the state could not be read. */
export const SUBSCRIPTION_LOAD_ERROR_MESSAGE =
  "We couldn't check your subscription. Reload the page to try again.";

/** Shown in place of Subscribe to someone whose access is not Stripe-billed. */
export const ALREADY_HAVE_ACCESS_MESSAGE = 'You already have access.';

// A reason this client does not know reads as `existing_subscription`: the
// portal is where both are resolved.
function toReason(value: unknown): CheckoutConflictReason {
  return value === 'payment_required'
    ? 'payment_required'
    : 'existing_subscription';
}

/**
 * Reads the refusal off the error the API client threw. Used where that error
 * is still intact, in `purchaseProduct`.
 */
export function readCheckoutConflict(
  error: unknown
): { reason: CheckoutConflictReason } | null {
  if (typeof error !== 'object' || error === null) {
    return null;
  }
  const context = (error as { context?: unknown }).context;
  if (typeof context !== 'object' || context === null) {
    return null;
  }
  const { code, reason } = context as { code?: unknown; reason?: unknown };
  if (code !== HEAPSTERLIVE_SUBSCRIPTION_EXISTS) {
    return null;
  }
  return { reason: toReason(reason) };
}

/**
 * Reads the refusal off a failed purchase as `usePurchase` throws it: a
 * `PurchaseError` whose `code` is `HEAPSTERLIVE_SUBSCRIPTION_EXISTS`, with the
 * reason in `conflictReason`. Null for any other failure.
 */
export function purchaseConflictReason(
  error: unknown
): CheckoutConflictReason | null {
  if (typeof error !== 'object' || error === null) {
    return null;
  }
  const { code, conflictReason } = error as {
    code?: unknown;
    conflictReason?: unknown;
  };
  if (code !== HEAPSTERLIVE_SUBSCRIPTION_EXISTS) {
    return null;
  }
  return toReason(conflictReason);
}
